import { Currency } from '../../models/serializable/currency.js';
import { Price } from '../../models/serializable/price.js';
import { VersionRange } from '../../models/core/version-range.js';
import { cloneMap, convertMapEntries, DataModelBuilder,
  isSameData, isSameMap, mapToData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { PriceRange } from '../../models/serializable/price-range.js';
import { PriceType } from '../../models/serializable/price-type.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';

export class ServicePriceComponent extends Component {
  static readonly TYPE_ID: string = 'dfaa11194f284685ae94cb168b4375f7';

  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public readonly priceRanges: Map<Currency, PriceRange>,
    public readonly priceType: PriceType,
  ) {
    super();
  }

  get $removable(): boolean {
    return false;
  }

  get $typeId(): string {
    return ServicePriceComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(2, ServicePriceComponent.SCHEMA_VERSION);
  }

  static fromPrices(prices: Map<Currency, Price>): ServicePriceComponent {
    const priceRanges = convertMapEntries<Currency, Price, Currency, PriceRange>(prices, (k, v) => {
      return { key: k, value: PriceRange.exactly(v) };
    });

    return new ServicePriceComponent(priceRanges, PriceType.PricePerService);
  }

  static fromMap(context: SerializationContext, map: SerializedData): ServicePriceComponent {
    return new ServicePriceComponent(
        MapUtils.getMap<Currency, PriceRange>(map, 'priceRanges', (k, v) => {
          return { key: <Currency>k, value: context.fromData<PriceRange>(v as SerializedData) };
        }),
        toEnumFromString(MapUtils.get<string>(map, 'priceType'), PriceType),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'priceRanges': mapToData(this.priceRanges, (k, v) => {
        return { key: k.toString(), value: context.toData(v) };
      }),
      'priceType': fromEnumAsString(this.priceType),
    };
  }

  clone(): this {
    return new ServicePriceComponent(
        cloneMap(this.priceRanges),
        this.priceType,
    ) as this;
  }

  isSame(other: ServicePriceComponent) {
    if(this === other) {
      return true;
    }

    return isSameMap(other.priceRanges, this.priceRanges, isSameData) &&
      other.priceType === this.priceType;
  }
}

export class ServicePriceComponentBuilder extends DataModelBuilder<ServicePriceComponent> {
  build(context: SerializationContext, map: SerializedData): ServicePriceComponent {
    return ServicePriceComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, 'priceType', PriceType, PriceType.PricePerService);

    if(versionRange.contains(ServicePriceComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(ServicePriceComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `prices` of type [Map<Currency, Price>] was changed
    // to `priceRanges` of type [Map<Currency, PriceRange>].
    if(version < 2) {
      const oldPrices = MapUtils.getMap<Currency, Price>(map, 'prices', (k, v) => {
        return { key: <Currency>k, value: Price.decode(v as string) };
      });
      const priceRanges = convertMapEntries<Currency, Price, Currency, PriceRange>(oldPrices, (k, v) => {
        return { key: k, value: PriceRange.exactly(v) };
      });

      MapUtils.set(map, 'priceRanges', mapToData(priceRanges, (k, v) => {
        return { key: k.toString(), value: context.toData(v) };
      }));
      version = 2;
    }

    // / In this version 'priceType' of [PriceType] was introduced.
    const priceType = MapUtils.get<PriceType>(map, 'priceType');
    if(version < 3 || !priceType) {
      MapUtils.set(map, 'priceType', PriceType.PricePerService);
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
